// Every App Banners Part 2 API call and its types, in one place: when the server's shapes
// move, this is the only file that has to follow. All of these need the App Banners tick on
// the project asked about, or the send's own.

import api, { type AnnouncementAudience } from '../../services/api';

type Ok<T> = { success: boolean; data: T };

export type DetailScope = 'store' | 'employee';
export type DetailSource = 'column' | 'custom_field';

/** Names a store or employee detail, as the rules and the targets both name it. */
export interface DetailRef {
  scope: DetailScope;
  source: DetailSource;
  key: string;
}

/** One detail a project can filter on: GET /announcements/details/options. */
export interface BannerDetailField extends DetailRef {
  label: string;
  /** 'text' | 'number' | 'date' | 'boolean' | 'dropdown' | 'multiple_choice' */
  kind: string;
  /** What this project's people or stores hold. Empty for a date or a tenure. */
  values: string[];
  /** More values exist than are listed; the server matches a typed one too. */
  valuesTruncated: boolean;
  /** Store Category only: the target each value's box starts at. */
  seedLevels?: Record<string, number>;
}

/** A detail the text can hold, such as {{store.name}}. */
export interface BannerToken {
  token: string;
  label: string;
  group: 'Person' | 'Store' | 'Employee' | 'Task';
  /** Only a Reach a level (or Reach a score) can fill it. */
  figureOnly?: boolean;
}

export interface BannerDetailOptions {
  fields: BannerDetailField[];
  tokens: BannerToken[];
}

/** One By details rule. Several values mean any of them; the rules all have to fit. */
export interface DetailRule extends DetailRef {
  op: 'in';
  values: string[];
}

/** The audience of a send or a count: the three of today, or By details. */
export type BannerAudienceBody = AnnouncementAudience | { detailRules: DetailRule[] };

/** A form from Master Data › Tasks. `warning`: the server's words, when it has something to warn of. */
export interface TaskFormOption {
  id: string;
  name: string;
  warning: string | null;
  /** The same warning in one line, also the server's words. */
  warningShort: string | null;
  /** Opens only from the banner, with no started day. Absent: it needs a started day. */
  bannerOnly?: boolean;
}

/** One thing Reach a level can check: GET /announcements/reach-level/sources. */
export interface LevelSourceOption {
  /** Sent back as reachLevel.source, with `month` added where needsMonth is true. */
  source: Record<string, unknown>;
  label: string;
  group: string;
  needsMonth: boolean;
  /** Text rather than a number: the only target is has_value. */
  hasValueOnly: boolean;
}

export interface OwnTargetOption extends DetailRef {
  label: string;
}

export interface ReachLevelSources {
  sources: LevelSourceOption[];
  /** Details whose value can be someone's own target number. */
  ownTargets: OwnTargetOption[];
}

export type LevelTargetInput =
  | { mode: 'fixed'; value: number }
  | { mode: 'own'; field: DetailRef }
  | { mode: 'per_value'; field: DetailRef; values: { value: string; target: number }[] }
  | { mode: 'has_value' };

/** The send's and the preview's `reachLevel`. */
export interface ReachLevelInput {
  source: Record<string, unknown>;
  target: LevelTargetInput;
}

export interface ReachLevelPreview {
  total: number;
  belowTarget: number;
  alreadyMet: number;
  /** Left out of a send: nothing gives them a target. */
  noTarget: number;
  /** Of belowTarget, those with no figure at all yet. */
  noFigure: number;
  /** 'score_target' when this is exactly today's Reach a score. */
  storedAs: string;
}

/** The chosen source as a send stores it, on a Sent history row and a tracker. */
export interface StoredLevelSource {
  kind: string;
  metricName?: string;
  month?: string;
  metric?: string;
  moduleTitle?: string;
  field?: { label?: string };
}

/** What a Reach a level send checks, as stored. */
export interface StoredLevel {
  source: StoredLevelSource;
  target: { mode: LevelTargetInput['mode']; valueCount?: number };
}

export type AfterDue = 'take_off' | 'keep_showing';

export type TrackerStatus = 'Done' | 'Pending' | 'Overdue' | 'Declined';

/**
 * What someone was shown when they answered, details filled in. The server stores it only if it
 * is what their phone last read, so this is what was on their screen.
 */
export interface AnswerWording {
  title: string;
  message: string;
  primaryLabel: string | null;
  secondaryLabel: string | null;
}

export interface TrackerPerson {
  empCode: string;
  name: string | null;
  store: string | null;
  storeCode: string | null;
  /** null on a send with nothing to do, and for someone a Read this reached only as a plain banner. */
  status: TrackerStatus | null;
  seen: boolean;
  firstSeenAt: string | null;
  doneAt: string | null;
  /** They saw it on an app that does not list this task: as a plain banner. */
  olderApp: boolean;
  figure: number | string | null;
  target: number | null;
  /** Below: only on a send that takes an answer. */
  answer?: 'agreed' | 'declined' | 'got_it' | null;
  answeredAt?: string | null;
  wording?: AnswerWording | null;
  appVersion?: string | null;
  platform?: string | null;
  /**
   * What named them, from the server's own check of their sign-in. 'app-pass': their OTP sign-in
   * on that phone. It shows whose sign-in it was, not who was holding the phone.
   */
  callerProof?: 'app-pass' | 'web-pass' | 'session' | 'own-token' | null;
}

export interface BatchTracker {
  send: {
    batchId: string;
    title: string;
    body: string;
    kind: 'message' | 'task';
    taskType: string | null;
    projectId: number | null;
    createdBy: string | null;
    /** The sender as people read them: "Asha Menon (100231)", or the code alone. */
    sentBy: string | null;
    createdAt: string;
    expiresAt: string;
    revokedAt: string | null;
    active: boolean;
    completeBy: string | null;
    afterDue: AfterDue | null;
    fillForm?: { formName: string };
    scoreTarget?: { metricName: string; month: string };
    reachLevel?: StoredLevel;
  };
  /** false: Seen and Not seen are all there is. */
  tracksDone: boolean;
  /** Its answers are kept: each person carries answer, answeredAt and the wording. */
  takesAnswer?: boolean;
  /** It cannot be done at all on an older app, so Older app means "must update". */
  needsNewApp?: boolean;
  /** false: sent before Seen was kept, so nobody's row can say whether they saw it. */
  seenTracked: boolean;
  counts: {
    total: number;
    done: number;
    pending: number;
    overdue: number;
    seen: number;
    notSeen: number;
    olderApp: number;
    declined?: number;
  };
  people: TrackerPerson[];
  /** The live figures could not be read: statuses are as last stored, figures blank. */
  figuresUnavailable: boolean;
}

/** Has the app build that draws buttons reached both stores? `reason`: why not, in the page's words. */
export interface ActionsRelease {
  released: boolean;
  reason: string | null;
}

export const bannerAPI = {
  actionsRelease: () => api.get<Ok<ActionsRelease>>('/announcements/actions-release'),
  detailOptions: (projectId: number) =>
    api.get<Ok<BannerDetailOptions>>('/announcements/details/options', { params: { projectId } }),
  // Writes and sends nothing; the send works it out again for itself.
  matchDetails: (body: { projectId: number; rules: DetailRule[] }) =>
    api.post<Ok<{ count: number }>>('/announcements/details/match', body),
  taskForms: (projectId: number) =>
    api.get<Ok<TaskFormOption[]>>('/announcements/task-forms', { params: { projectId } }),
  reachLevelSources: (projectId: number) =>
    api.get<Ok<ReachLevelSources>>('/announcements/reach-level/sources', { params: { projectId } }),
  // Writes and sends nothing. 400 until what to check and the target are valid.
  reachLevelPreview: (body: { projectId: number; reachLevel: ReachLevelInput } & BannerAudienceBody) =>
    api.post<Ok<ReachLevelPreview>>('/announcements/reach-level/preview', body),
  tracker: (batchId: string) => api.get<Ok<BatchTracker>>(`/announcements/history/${batchId}/tracker`),
};
